/**
 * Plan 15, Task 4.2 — Dice throw character animation (gsap timeline, ≤120 lines)
 *
 * Animates the character's root rotation to simulate throwing a die:
 *   0.00–0.30 s  WIND-UP  — rotate OPPOSITE of throwDirection.x sign by 0.5 rad (power2.out)
 *   0.30–0.50 s  THROW    — reverse to +0.5 rad in throwDirection.x direction (power2.in)
 *   0.50–0.65 s  SETTLE   — ease back to rotation.y = 0 (power2.out)
 *
 * NaN guard: throws before registry.create if throwDirection.x is non-finite
 *            OR if all direction components are zero.
 * tl.call used for onComplete (NOT eventCallback) — sentinel forward-defense per Tasks 3.1/3.2/4.1.
 * cancel() idempotent (cancelled flag); onComplete NOT called on cancel.
 * Auto-disposes AnimationHandle AFTER onComplete returns (completed flag pattern).
 *
 * NOTE: The 3D die mesh + physics is OUT OF SCOPE — Plan 15 ships ONLY the character animation.
 */

import type { CharacterRig } from '../character/types';
import type { AnimationRegistry, AnimationHandle } from './registry';
import type { GsapRegistry, GsapTimelineHandle } from '../vfx/gsap-registry';
import * as THREE from 'three';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface DiceThrowConfig {
  readonly rig: CharacterRig;
  readonly registry: AnimationRegistry;
  readonly throwDirection: { x: number; y: number; z: number };
  onComplete?: () => void;
}

export interface DiceThrowHandle {
  readonly animation: AnimationHandle;
  cancel(): void;
}

export interface ProceduralD20RollConfig {
  readonly scene: THREE.Object3D;
  readonly gsapRegistry: GsapRegistry;
  readonly catchCenterPosition: THREE.Vector3;
  readonly result: number;
  readonly durationSeconds?: number;
  readonly catchScale?: number;
  readonly catchScaleAtSeconds?: number;
  readonly catchPopScale?: number;
  onImpact?: () => void;
}

export interface ProceduralD20RollHandle {
  readonly root: THREE.Group;
  readonly finished: Promise<void>;
  dispose(): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Animation constants
// ─────────────────────────────────────────────────────────────────────────────

const WIND_UP_END = 0.3;
const THROW_END = 0.5;
const SETTLE_END = 0.65;
const ROTATION_AMP = 0.5; // radians
const D20_RADIUS = 0.62;
const D20_DEFAULT_DURATION = 2.75;
const D20_CATCH_Y = 0;
const D20_RESULT_TEXTURE_SIZE = 192;
const D20_FACE_TEXTURE_SIZE = 160;
const D20_NUMBER_UVS = new Float32Array([0.5, 1.18, -0.18, -0.18, 1.18, -0.18]);
const D20_NUMBER_MAX_WIDTH_RATIO = 0.58;
const D20_NUMBER_MAX_HEIGHT_RATIO = 0.46;

interface D20FaceNumberResource {
  readonly geometry: THREE.BufferGeometry;
  readonly material: THREE.MeshBasicMaterial;
  readonly texture: THREE.Texture;
  readonly mesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
  readonly value: number;
  readonly isResultFace: boolean;
}

const D20_UP = new THREE.Vector3(0, 1, 0);

// ─────────────────────────────────────────────────────────────────────────────
// Public factory
// ─────────────────────────────────────────────────────────────────────────────

export function spawnDiceThrow(config: DiceThrowConfig): DiceThrowHandle {
  const { rig, registry, throwDirection, onComplete } = config;

  // NaN guard — validate BEFORE touching the registry.
  if (
    !Number.isFinite(throwDirection.x) ||
    !Number.isFinite(throwDirection.y) ||
    !Number.isFinite(throwDirection.z)
  ) {
    throw new Error(
      'spawnDiceThrow: throwDirection contains non-finite component(s) — animation aborted.',
    );
  }
  if (throwDirection.x === 0 && throwDirection.y === 0 && throwDirection.z === 0) {
    throw new Error(
      'spawnDiceThrow: throwDirection is the zero vector — sign is undefined, animation aborted.',
    );
  }

  // Derive the rotation sign from the throw direction's x component.
  // sign > 0: throw goes right  → wind-up goes left (negative y rotation)
  // sign < 0: throw goes left   → wind-up goes right (positive y rotation)
  // If throwDirection.x is exactly 0, treat sign as +1 (arbitrary, non-zero guard passed above).
  const dirSign = throwDirection.x === 0 ? 1 : Math.sign(throwDirection.x);
  const windUpY = -dirSign * ROTATION_AMP; // opposite of throw direction
  const throwY = dirSign * ROTATION_AMP; // in throw direction

  const handle = registry.create(rig, 'dice-throw');
  const tl = handle.timeline;

  // WIND-UP: 0..0.30 s — rotate to windUpY (opposite of throw direction)
  tl.to(rig.root.rotation, { y: windUpY, duration: WIND_UP_END, ease: 'power2.out' }, 0);

  // THROW: 0.30..0.50 s — reverse to throwY (in throw direction)
  tl.to(
    rig.root.rotation,
    { y: throwY, duration: THROW_END - WIND_UP_END, ease: 'power2.in' },
    WIND_UP_END,
  );

  // SETTLE: 0.50..0.65 s — ease back to rotation.y = 0
  tl.to(
    rig.root.rotation,
    { y: 0, duration: SETTLE_END - THROW_END, ease: 'power2.out' },
    THROW_END,
  );

  // tl.call instead of eventCallback('onComplete') — gsap-registry sentinel extends to 3600s.
  let completed = false;
  tl.call(
    () => {
      if (completed) return;
      completed = true;
      onComplete?.();
      handle.dispose(); // auto-dispose after onComplete returns
    },
    undefined,
    SETTLE_END,
  );

  let cancelled = false;

  return {
    get animation() {
      return handle;
    },
    cancel() {
      if (cancelled) return; // idempotent
      cancelled = true;
      completed = true; // prevent tl.call() from firing after dispose
      handle.dispose();
    },
  };
}

function measuredTextHeight(metrics: TextMetrics, fallback: number): number {
  const ascent = metrics.actualBoundingBoxAscent;
  const descent = metrics.actualBoundingBoxDescent;
  if (Number.isFinite(ascent) && Number.isFinite(descent) && ascent + descent > 0) {
    return ascent + descent;
  }
  return fallback;
}

function createNumberTexture(value: number, size = D20_FACE_TEXTURE_SIZE): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx == null) {
    throw new Error('createNumberTexture: 2D canvas context unavailable');
  }

  ctx.clearRect(0, 0, size, size);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = '#5b198f';
  ctx.fillStyle = '#ffffff';
  const text = String(value);
  const maxWidth = size * D20_NUMBER_MAX_WIDTH_RATIO;
  const maxHeight = size * D20_NUMBER_MAX_HEIGHT_RATIO;
  let fontSize = Math.floor(size * 0.48);
  for (; fontSize >= Math.floor(size * 0.24); fontSize -= 2) {
    ctx.font = `900 ${fontSize}px Arial Black, sans-serif`;
    const lineWidth = Math.max(3, fontSize * 0.08);
    const metrics = ctx.measureText(text);
    const height = measuredTextHeight(metrics, fontSize);
    if (metrics.width + lineWidth * 2 <= maxWidth && height + lineWidth * 2 <= maxHeight) {
      break;
    }
  }
  ctx.font = `900 ${fontSize}px Arial Black, sans-serif`;
  ctx.lineWidth = Math.max(3, fontSize * 0.08);
  ctx.strokeText(text, size / 2, size / 2);
  ctx.fillText(text, size / 2, size / 2);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

function createNonIndexedIcosahedronGeometry(radius: number): THREE.BufferGeometry {
  const geometry = new THREE.IcosahedronGeometry(radius, 0);
  if (geometry.index == null) return geometry;
  const nonIndexed = geometry.toNonIndexed();
  geometry.dispose();
  return nonIndexed;
}

function createD20FaceValues(result: number): number[] {
  const safeResult = Number.isInteger(result) && result >= 1 && result <= 20 ? result : 1;
  return [
    safeResult,
    ...Array.from({ length: 20 }, (_, index) => index + 1).filter((value) => value !== safeResult),
  ];
}

function getD20FaceNormal(faceIndex: number): THREE.Vector3 {
  const geometry = createNonIndexedIcosahedronGeometry(D20_RADIUS);
  const positions = geometry.getAttribute('position');
  const vertexIndex = faceIndex * 3;
  if (vertexIndex + 2 >= positions.count) {
    geometry.dispose();
    return D20_UP.clone();
  }

  const a = new THREE.Vector3().fromBufferAttribute(positions, vertexIndex);
  const b = new THREE.Vector3().fromBufferAttribute(positions, vertexIndex + 1);
  const c = new THREE.Vector3().fromBufferAttribute(positions, vertexIndex + 2);
  const normal = a
    .add(b)
    .add(c)
    .multiplyScalar(1 / 3)
    .normalize();
  geometry.dispose();
  return normal;
}

function addD20FaceNumbers(root: THREE.Group, result: number): D20FaceNumberResource[] {
  const resources: D20FaceNumberResource[] = [];
  const faceGeometry = createNonIndexedIcosahedronGeometry(D20_RADIUS);
  const positions = faceGeometry.getAttribute('position');
  const faceValues = createD20FaceValues(result);
  const center = new THREE.Vector3();
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const faceLift = new THREE.Vector3();
  const insetA = new THREE.Vector3();
  const insetB = new THREE.Vector3();
  const insetC = new THREE.Vector3();

  for (let faceIndex = 0; faceIndex < positions.count / 3; faceIndex++) {
    a.fromBufferAttribute(positions, faceIndex * 3);
    b.fromBufferAttribute(positions, faceIndex * 3 + 1);
    c.fromBufferAttribute(positions, faceIndex * 3 + 2);
    center
      .copy(a)
      .add(b)
      .add(c)
      .multiplyScalar(1 / 3);
    const normal = center.clone().normalize();
    const value = faceValues[faceIndex] ?? faceIndex + 1;
    const texture = createNumberTexture(
      value,
      faceIndex === 0 ? D20_RESULT_TEXTURE_SIZE : D20_FACE_TEXTURE_SIZE,
    );
    const inset = faceIndex === 0 ? 0.64 : 0.54;
    faceLift.copy(normal).multiplyScalar(0.018);
    insetA.copy(center).add(a.clone().sub(center).multiplyScalar(inset)).add(faceLift);
    insetB.copy(center).add(b.clone().sub(center).multiplyScalar(inset)).add(faceLift);
    insetC.copy(center).add(c.clone().sub(center).multiplyScalar(inset)).add(faceLift);

    const numberGeometry = new THREE.BufferGeometry();
    numberGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(
        new Float32Array([
          insetA.x,
          insetA.y,
          insetA.z,
          insetB.x,
          insetB.y,
          insetB.z,
          insetC.x,
          insetC.y,
          insetC.z,
        ]),
        3,
      ),
    );
    numberGeometry.setAttribute('uv', new THREE.BufferAttribute(D20_NUMBER_UVS.slice(), 2));
    numberGeometry.computeVertexNormals();

    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      side: THREE.DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -4,
      polygonOffsetUnits: -4,
      depthWrite: false,
    });
    const numberMesh = new THREE.Mesh(numberGeometry, material);
    numberMesh.name = faceIndex === 0 ? 'd20-result-face-number' : 'd20-face-number';
    numberMesh.userData.d20Value = value;
    numberMesh.userData.isResultFace = faceIndex === 0;
    root.add(numberMesh);
    resources.push({
      geometry: numberGeometry,
      material,
      texture,
      mesh: numberMesh,
      value,
      isResultFace: faceIndex === 0,
    });
  }

  faceGeometry.dispose();
  return resources;
}

export function spawnProceduralD20Roll(config: ProceduralD20RollConfig): ProceduralD20RollHandle {
  const duration = config.durationSeconds ?? D20_DEFAULT_DURATION;
  const catchScale =
    config.catchScale != null && Number.isFinite(config.catchScale)
      ? THREE.MathUtils.clamp(config.catchScale, 0.35, 1.25)
      : null;
  const catchScaleAtSeconds =
    catchScale == null
      ? null
      : THREE.MathUtils.clamp(config.catchScaleAtSeconds ?? duration - 0.18, 0.08, duration);
  const catchPopScale =
    catchScale == null || config.catchPopScale == null || !Number.isFinite(config.catchPopScale)
      ? null
      : THREE.MathUtils.clamp(config.catchPopScale, catchScale, 1.35);
  const root = new THREE.Group();
  root.name = 'procedural-d20-roll';
  root.position.copy(config.catchCenterPosition);
  root.scale.setScalar(0.85);

  const geometry = new THREE.IcosahedronGeometry(D20_RADIUS, 0);
  const material = new THREE.MeshStandardMaterial({
    color: 0x9a35e8,
    emissive: 0x6e1fb8,
    emissiveIntensity: 0.45,
    roughness: 0.36,
    metalness: 0.08,
    flatShading: true,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'procedural-d20-mesh';
  root.add(mesh);

  const edgeGeometry = new THREE.EdgesGeometry(geometry);
  const edgeMaterial = new THREE.LineBasicMaterial({
    color: 0xe6bbff,
    transparent: true,
    opacity: 0.45,
  });
  const edges = new THREE.LineSegments(edgeGeometry, edgeMaterial);
  edges.name = 'procedural-d20-edges';
  root.add(edges);

  const numberResources = addD20FaceNumbers(root, config.result);
  const resultNumberResource = numberResources.find((resource) => resource.isResultFace);

  config.scene.add(root);

  const proxy = {
    spin: 0,
    tilt: 0,
    roll: 0,
    y: D20_CATCH_Y,
    scale: 0.85,
  };
  let disposed = false;
  let handle: GsapTimelineHandle | null = null;

  const finished = new Promise<void>((resolve) => {
    handle = config.gsapRegistry.create('procedural-d20:roll');
    const resultFaceNormal = getD20FaceNormal(0);
    const targetQuaternion = new THREE.Quaternion().setFromUnitVectors(resultFaceNormal, D20_UP);
    const targetEuler = new THREE.Euler().setFromQuaternion(targetQuaternion, 'XYZ');
    proxy.tilt = targetEuler.x + Math.PI * 6;
    proxy.spin = targetEuler.y + Math.PI * 10;
    proxy.roll = targetEuler.z + Math.PI * 8;

    const apply = (): void => {
      root.position.copy(config.catchCenterPosition).add(new THREE.Vector3(0, proxy.y, 0));
      root.rotation.set(proxy.tilt, proxy.spin, proxy.roll, 'XYZ');
      root.scale.setScalar(proxy.scale);
    };
    apply();

    handle.timeline.to(
      proxy,
      {
        spin: targetEuler.y,
        tilt: targetEuler.x,
        roll: targetEuler.z,
        y: D20_CATCH_Y,
        duration,
        ease: 'power3.out',
        onUpdate: apply,
      },
      0,
    );
    if (catchScale == null || catchScaleAtSeconds == null) {
      handle.timeline.to(
        proxy,
        {
          scale: 1,
          duration,
          ease: 'power3.out',
          onUpdate: apply,
        },
        0,
      );
    } else {
      handle.timeline.to(
        proxy,
        {
          scale: 1,
          duration: Math.min(0.42, catchScaleAtSeconds),
          ease: 'sine.out',
          onUpdate: apply,
        },
        0,
      );
      handle.timeline.to(
        proxy,
        {
          scale: catchScale,
          duration: Math.max(0.06, duration - catchScaleAtSeconds),
          ease: 'power2.out',
          onUpdate: apply,
        },
        catchScaleAtSeconds,
      );
      if (catchPopScale != null && catchPopScale > catchScale + 0.001) {
        handle.timeline.to(
          proxy,
          {
            scale: catchPopScale,
            duration: 0.06,
            ease: 'sine.out',
            onUpdate: apply,
          },
          duration,
        );
        handle.timeline.to(
          proxy,
          {
            scale: catchScale,
            duration: 0.14,
            ease: 'sine.inOut',
            onUpdate: apply,
          },
          duration + 0.06,
        );
      }
    }
    if (resultNumberResource != null) {
      handle.timeline.to(
        resultNumberResource.material.color,
        {
          r: 1,
          g: 0.93,
          b: 0.35,
          duration: 0.08,
          ease: 'sine.out',
        },
        duration - 0.18,
      );
      handle.timeline.to(
        resultNumberResource.material.color,
        {
          r: 1,
          g: 1,
          b: 1,
          duration: 0.16,
          ease: 'sine.in',
        },
        duration - 0.1,
      );
    }
    handle.timeline.call(
      () => {
        config.onImpact?.();
        resolve();
      },
      undefined,
      duration,
    );
  });

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    handle?.dispose();
    config.scene.remove(root);
    for (const resource of numberResources) {
      resource.geometry.dispose();
      resource.material.dispose();
      resource.texture.dispose();
    }
    geometry.dispose();
    material.dispose();
    edgeGeometry.dispose();
    edgeMaterial.dispose();
  }

  return { root, finished, dispose };
}
